<!-- Verträge (company, read-only). The page shell is the documented one — layout-page-container owns the
     gutter, layout-page-scrollable is the single scroll container; see docs/articles/frontend/page-layout-contract.md.
     Everything inside follows the card grammar of the newest screens (Owner → Plattform #608, Zugänge #568):
     one white section card per question, a tinted card for what changes next. -->
<layout-page-container>
  <layout-header-container title="Verträge"/>

  <layout-page-scrollable>
    @if (company) {
      <div class="space-y-4">

        <p class="max-w-prose text-sm text-gray-500">
          Laufzeit und gebuchte Kontingente Ihres Unternehmens. Änderungen am Vertrag nimmt die
          Plattformverwaltung vor.
        </p>

        <!-- ── Aktueller Vertrag ──────────────────────────────────────────────────────────────────────────
             The three quantities are tiles rather than table rows: each carries a different SHAPE of fact
             (two of them a used/limit pair with a meter, one a bare allowance), and a table would have to
             leave half its cells empty to hold them in one column set. -->
        <section aria-labelledby="contract-current-title"
                 class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
          <h2 class="text-base font-bold tracking-tight text-gray-800" id="contract-current-title">
            Aktueller Vertrag
          </h2>

          @if (current) {
            <!-- Beginn and Ende as one labelled span: two separate stat boxes spent a third of the card on
                 two dates nobody compares to anything. -->
            <p class="mt-1 text-sm text-gray-500">
              Laufzeit
              <span class="font-semibold text-gray-900">
                {{ current.validFrom | dayKey:'long' }} bis {{ current.validTo | dayKey:'long' }}
              </span>
            </p>

            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">

              <!-- Standorte: used (active shards this billing month) against the contracted limit.

                   `gap-2` rather than a margin per row: the rows are flex items, so the gap survives the
                   `mt-auto` that pins a tile's last row to its bottom edge — a margin would be replaced by it
                   and the rule would end up flush against the meter above it. -->
              <div class="flex h-full flex-col gap-2 rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3"
                   data-testid="tile-shards">
                <div class="flex items-center gap-2">
                  <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                               bg-theme-primary-50 text-theme-primary-600">
                    <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 16 16">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"
                            fill="currentColor"/>
                    </svg>
                  </span>
                  <span class="text-sm font-bold tracking-tight text-gray-700">Standorte</span>
                </div>

                <p class="flex items-baseline gap-1.5">
                  <span class="text-2xl font-bold leading-none tabular-nums text-gray-900">
                    {{ usageLabel(company.customerValues.item1?.shardCount) }}
                  </span>
                  <span class="text-sm font-semibold tabular-nums text-gray-500">
                    / {{ limitLabel(current.shardCount) }}
                  </span>
                </p>

                @if (hasMeter(current.shardCount)) {
                  <!-- Decorative on purpose: the two figures above ARE the fact, so a second progressbar role
                       would read the same number twice to a screen reader. -->
                  <div aria-hidden="true" class="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div [class.bg-red-500]="meterOver(company.customerValues.item1?.shardCount, current.shardCount)"
                         [class.bg-theme-primary-500]="!meterOver(company.customerValues.item1?.shardCount, current.shardCount)"
                         [style.width.%]="meterPct(company.customerValues.item1?.shardCount, current.shardCount)"
                         class="h-full rounded-full transition-all duration-300"></div>
                  </div>
                }

                @if (meterOver(company.customerValues.item1?.shardCount, current.shardCount)) {
                  <!-- Colour alone cannot carry this: the red bar needs the sentence beside it. -->
                  <p>
                    <span class="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs
                                 font-semibold text-red-700">Kontingent überschritten</span>
                  </p>
                }
              </div>

              <!-- Registrierte Geräte: the billed figure of the running month, plus the one already known for
                   the next — a device registered today keeps billing into the coming month. -->
              <div class="flex h-full flex-col gap-2 rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3"
                   data-testid="tile-devices">
                <div class="flex items-center gap-2">
                  <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                               bg-theme-primary-50 text-theme-primary-600">
                    <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 16 16">
                      <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"
                            fill="currentColor"/>
                      <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2" fill="currentColor"/>
                    </svg>
                  </span>
                  <span class="text-sm font-bold tracking-tight text-gray-700">Registrierte Geräte</span>
                </div>

                <p class="flex items-baseline gap-1.5">
                  <span class="text-2xl font-bold leading-none tabular-nums text-gray-900">
                    {{ usageLabel(company.customerValues.item1?.deviceCount) }}
                  </span>
                  <span class="text-sm font-semibold tabular-nums text-gray-500">
                    / {{ limitLabel(current.deviceCount) }}
                  </span>
                </p>

                @if (hasMeter(current.deviceCount)) {
                  <div aria-hidden="true" class="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div [class.bg-red-500]="meterOver(company.customerValues.item1?.deviceCount, current.deviceCount)"
                         [class.bg-theme-primary-500]="!meterOver(company.customerValues.item1?.deviceCount, current.deviceCount)"
                         [style.width.%]="meterPct(company.customerValues.item1?.deviceCount, current.deviceCount)"
                         class="h-full rounded-full transition-all duration-300"></div>
                  </div>
                }

                @if (meterOver(company.customerValues.item1?.deviceCount, current.deviceCount)) {
                  <p>
                    <span class="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs
                                 font-semibold text-red-700">Kontingent überschritten</span>
                  </p>
                }

                @if (deviceUsageChanges) {
                  <!-- `mt-auto`: the tiles stretch to the tallest of the row, so pinning the delta line to the
                       bottom keeps its rule level with the one in the tile next door instead of floating a
                       meter's height above it. -->
                  <p class="mt-auto border-t border-gray-100 pt-2 text-xs text-gray-500">
                    ab {{ nDay | dayKey }}:
                    <span class="font-semibold tabular-nums text-gray-700">
                      {{ usageLabel(company.customerValues.item2?.deviceCount) }} / {{ limitLabel(next?.deviceCount) }}
                    </span>
                  </p>
                }
              </div>

              <!-- #664: parallel virtual events. No usage figure and therefore no meter — the limit is
                   company-wide AND window-bound (a sweep-line maximum over the calendar), so a single "in
                   use" number would be wrong on most days. The "ab …" line mirrors the device tile's:
                   the event gate resolves the contract covering the event's START, so an event planned for
                   next month is licensed by `next`, not by `current`. -->
              <div class="flex h-full flex-col gap-2 rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3"
                   data-testid="tile-virtual-events">
                <div class="flex items-center gap-2">
                  <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                               bg-theme-primary-50 text-theme-primary-600">
                    <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 16 16">
                      <path d="M3.05 3.05a7 7 0 0 0 0 9.9.5.5 0 0 1-.707.707 8 8 0 0 1 0-11.314.5.5 0 0 1 .707.707m2.122 2.122a4 4 0 0 0 0 5.656.5.5 0 1 1-.708.708 5 5 0 0 1 0-7.072.5.5 0 0 1 .708.708m5.656-.708a.5.5 0 0 1 .708 0 5 5 0 0 1 0 7.072.5.5 0 1 1-.708-.708 4 4 0 0 0 0-5.656.5.5 0 0 1 0-.708m2.122-2.12a.5.5 0 0 1 .707 0 8 8 0 0 1 0 11.313.5.5 0 0 1-.707-.707 7 7 0 0 0 0-9.9.5.5 0 0 1 0-.707M10 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0"
                            fill="currentColor"/>
                    </svg>
                  </span>
                  <span class="text-sm font-bold tracking-tight text-gray-700">Virtuelle Events (parallel)</span>
                </div>

                <p class="flex items-baseline gap-1.5">
                  <span class="text-2xl font-bold leading-none tabular-nums text-gray-900">
                    {{ limitLabel(current.virtualEventCount) }}
                  </span>
                  <span class="text-sm text-gray-500">gleichzeitig</span>
                </p>

                @if (virtualEventCountChanges) {
                  <p class="mt-auto border-t border-gray-100 pt-2 text-xs text-gray-500">
                    ab {{ nDay | dayKey }}:
                    <span class="font-semibold tabular-nums text-gray-700">
                      {{ limitLabel(next?.virtualEventCount) }}
                    </span>
                  </p>
                }
              </div>

            </div>
          } @else {
            <!-- No contract covers today. Naming the consequence is the useful thing here: this reader cannot
                 act on the page, so an "invitation to act" would be a dead end. -->
            <div class="mt-3 rounded-xl border-2 border-dashed border-gray-100 px-4 py-6 text-center"
                 data-testid="no-current-contract">
              <p class="text-sm font-bold text-gray-800">Kein laufender Vertrag</p>
              <p class="mt-1 text-sm text-gray-500">
                Ohne laufenden Vertrag können keine Geräte registriert und keine virtuellen Events angelegt
                werden.
              </p>
            </div>
          }
        </section>

        <!-- ── Ab <Datum> ─────────────────────────────────────────────────────────────────────────────────
             Only when a DIFFERENT contract takes over. Tinted (`theme-primary-50/50`), because it is the one
             thing on the page that is not yet true — the same accent the Einladen block on Zugänge uses.
             The heading carries the contract's OWN start date, not the 1st of next month: those two can be
             days apart, and the tiles above already state the billing-month boundary. -->
        @if (upcoming) {
          <section aria-labelledby="contract-next-title" data-testid="upcoming-contract"
                   class="rounded-2xl border-[1px] border-theme-primary-100/50 bg-theme-primary-50/50 p-4">
            <h2 class="text-base font-bold tracking-tight text-gray-800" id="contract-next-title">
              Ab {{ upcoming.validFrom | dayKey:'long' }}
            </h2>
            <p class="mt-1 text-sm text-gray-600">
              Laufzeit
              <span class="font-semibold text-gray-900">
                {{ upcoming.validFrom | dayKey:'long' }} bis {{ upcoming.validTo | dayKey:'long' }}
              </span>
            </p>

            <dl class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div class="rounded-xl border-[1px] border-theme-primary-100/50 bg-white p-3">
                <dt class="text-sm font-bold tracking-tight text-gray-700">Standorte</dt>
                <dd class="mt-1 text-2xl font-bold leading-none tabular-nums text-gray-900">
                  {{ limitLabel(upcoming.shardCount) }}
                </dd>
              </div>
              <div class="rounded-xl border-[1px] border-theme-primary-100/50 bg-white p-3">
                <dt class="text-sm font-bold tracking-tight text-gray-700">Registrierte Geräte</dt>
                <dd class="mt-1 text-2xl font-bold leading-none tabular-nums text-gray-900">
                  {{ limitLabel(upcoming.deviceCount) }}
                </dd>
              </div>
              <div class="rounded-xl border-[1px] border-theme-primary-100/50 bg-white p-3">
                <dt class="text-sm font-bold tracking-tight text-gray-700">Virtuelle Events (parallel)</dt>
                <dd class="mt-1 text-2xl font-bold leading-none tabular-nums text-gray-900">
                  {{ limitLabel(upcoming.virtualEventCount) }}
                </dd>
              </div>
            </dl>
          </section>
        }

        <!-- ── Vergangene Verträge ────────────────────────────────────────────────────────────────────────
             The same row component the owner screen lists contracts with, so one contract reads identically
             on both screens — with editing switched off, which is what makes this page read-only. -->
        @if (past.length) {
          <section aria-labelledby="contract-history-title">
            <h2 class="mb-2 flex items-center gap-2 text-base font-bold tracking-tight text-gray-800"
                id="contract-history-title">
              Vergangene Verträge
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                           font-semibold text-gray-600">{{ past.length }}</span>
            </h2>
            <div class="space-y-2">
              @for (contract of past; track contract.id) {
                <app-contract-information [canEdit]="false" [contract]="contract"
                                          badge="Beendet" badgeClass="bg-gray-100 text-gray-600"/>
              }
            </div>
          </section>
        }

      </div>
    } @else if (loaded) {
      <!-- The request came back without data. The German refusal itself goes to the app-wide banner
           (GlobalAlertService); what the page owes the reader is a body that is not blank. -->
      <div class="rounded-2xl border-2 border-dashed border-gray-100 px-4 py-6 text-center"
           data-testid="load-failed">
        <p class="text-sm font-bold text-gray-800">Keine Vertragsdaten</p>
        <p class="mt-1 text-sm text-gray-500">
          Die Vertragsdaten konnten nicht geladen werden. Bitte laden Sie die Seite neu.
        </p>
      </div>
    }
  </layout-page-scrollable>
</layout-page-container>
